import React from 'react';
import styles from './RegulationCard.module.css';

interface RegulationCardProps {
  id: string;
  title: string;
  snippet: string;
  source: string;
  date: string;
  url?: string;
  billType?: string | null;
  onSelect?: (id: string) => void;
  isSelected?: boolean;
  query?: string;
  aiDomainBadges?: string[];
  why?: string | null;   // SRS #168: why this result ranks here
  hasFullText?: boolean; // false when we hold the record but not the document
}

const RegulationCard: React.FC<RegulationCardProps> = ({
  id,
  title,
  snippet,
  onSelect,
  isSelected,
  date,
  query,
  aiDomainBadges = [],
  why,
  hasFullText,
}) => {
  // Generic words that shouldn't be highlighted in snippets — highlighting
  // "of"/"the" everywhere makes results look like they matched on noise.
  const HIGHLIGHT_STOPWORDS = new Set([
    'a', 'an', 'and', 'or', 'the', 'of', 'in', 'on', 'for', 'to', 'about', 'is',
    'are', 'with', 'by', 'from', 'what', 'which', 'show', 'me', 'list', 'all',
    'regulation', 'regulations', 'policy', 'policies', 'law', 'laws',
  ]);

  const highlightQuery = (text: string): string => {
    if (!query) return text;
    const plain = text.replace(/<[^>]*>/g, '');
    const words = query.trim().split(/\s+/)
      .filter(w => w.length > 1 && !HIGHLIGHT_STOPWORDS.has(w.toLowerCase()));
    if (words.length === 0) return plain;
    const pattern = words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
    return plain.replace(new RegExp(`(${pattern})`, 'gi'), '<mark>$1</mark>');
  };
  const openDetail = () => {
    window.open(`/regulations/${id}`, '_blank', 'noopener,noreferrer');
  };

  const handleClick = () => {
    if (onSelect) onSelect(id);
    openDetail();
  };

  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div
      className={`${styles.card} ${isSelected ? styles.selected : ''}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
    >
      <h3 className={styles.cardTitle}>{title}</h3>
      <span className={styles.date}>{formatDate(date)}</span>
      {hasFullText === false && (
        // Said plainly rather than hidden: the instrument is real and worth
        // knowing about, but a card that looks like every other one and has
        // nothing behind it is a dead end the reader only finds by clicking.
        <span className={styles.referenceOnly} data-testid="reference-only" title="We hold this instrument's record and a link to its source, but not its text.">
          Reference only — no full text held
        </span>
      )}
      {aiDomainBadges.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
          {aiDomainBadges.map(label => (
            <span
              key={label}
              style={{
                fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
                background: 'var(--ds-accent-soft, #eef0ff)', color: 'var(--ds-accent, var(--ds-brand-blue))',
              }}
            >
              {label}
            </span>
          ))}
        </div>
      )}
      <hr className={styles.divider} />
      <p className={styles.cardSnippet} dangerouslySetInnerHTML={{ __html: highlightQuery(snippet) }} />
      {why && <p data-testid="why" style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--ds-text-muted)' }} title="Why this result ranks here">Why: {why}</p>}
      <div className={styles.cardFooter}>
        <span className={styles.viewMore}>
          View details
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
            <polyline points="15 3 21 3 21 9"/>
            <line x1="10" y1="14" x2="21" y2="3"/>
          </svg>
        </span>
      </div>
    </div>
  );
};

export default RegulationCard;
